/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { atom } from 'jotai';
import { defaultRaceProgressionValue } from 'jotai/raceProgression';
import { activeEventSelector, activeSessionSelector } from 'jotai/session';
import { atomWithDefault, unwrap } from 'jotai/utils';
import {
  LapData,
  LapHistData,
  MessageInfo,
  QualifyingLapData,
  RaceProgressionData,
  RunType,
  SessionInfo,
  SessionTimingScoringData,
} from 'models/viewModels';
import { historicalRaceServiceServer } from 'services/historicalRace/HistoricalRaceService';
export const maxLapNumberSelector = atom<Promise<number>>(async (get) => {
  const raceProgressionReplayData = await get(raceProgressionReplayDataSelector);
  return raceProgressionReplayData && raceProgressionReplayData[0] ? raceProgressionReplayData[0].totalLaps : 1;
});

export const unwrappedMaxLapNumberSelector = unwrap(maxLapNumberSelector);

export const overwrittenLapReplayAtom = atomWithDefault<number | undefined>((get) => {
  return get(unwrappedMaxLapNumberSelector);
});

export const lapReplayControllerAtom = atom<number, [number], void>(
  (get) => {
    const overwrittenLapReplay = get(overwrittenLapReplayAtom);
    const unwrappedMaxLapNumber = get(unwrappedMaxLapNumberSelector);
    if (overwrittenLapReplay) {
      return overwrittenLapReplay;
    } else if (unwrappedMaxLapNumber) {
      return unwrappedMaxLapNumber;
    } else {
      return 1;
    }
  },
  (_get, set, newValue: number) => {
    set(overwrittenLapReplayAtom, newValue);
  }
);

export const raceProgressionReplayDataSelector = atom<Promise<RaceProgressionData[] | undefined>>(async (get) => {
  const eventId = get(activeEventSelector)?.eventId;
  const sessionType = get(activeSessionSelector)?.sessionType;
  const sessionId = get(activeSessionSelector)?.sessionId;
  return eventId && sessionType && sessionId
    ? await historicalRaceServiceServer.getReplaySessionProgressionData(eventId, sessionType, sessionId)
    : [];
});

export const unwrapRaceProgressionReplayDataSelector = unwrap(raceProgressionReplayDataSelector);

export const raceProgressionReplayCurrentLapSelector = atom<RaceProgressionData>((get) => {
  const lapReplayController = get(lapReplayControllerAtom);
  const currentLap = get(unwrapRaceProgressionReplayDataSelector)?.find(
    (item) => item.lapNumber === lapReplayController
  );
  return currentLap ? currentLap : defaultRaceProgressionValue;
});

const timingScoringReplayDataSelector = atom<Promise<SessionTimingScoringData[] | undefined>>(async (get) => {
  const eventId = get(activeEventSelector)?.eventId;
  const sessionType = get(activeSessionSelector)?.sessionType;
  const sessionId = get(activeSessionSelector)?.sessionId;

  return eventId && sessionType && sessionId
    ? await historicalRaceServiceServer.getReplaySessionTimingScoringData(eventId, sessionType, sessionId)
    : [];
});

const unwrappedTimingScoringReplayDataSelector = unwrap(timingScoringReplayDataSelector);

export const lapDataReplayCurrentLapSelector = atom<LapData[]>((get) => {
  const currentLap = get(unwrappedTimingScoringReplayDataSelector)?.find(
    (item) => item.leaderLap === get(lapReplayControllerAtom)
  );
  // each leader lap has a list of laps completed, need to show latest lap completed
  const vehicleLatestLapMap: Map<string, LapData> | undefined = currentLap?.sessionVehicleMetadataList.reduce(
    (map, item) => {
      const vehicleSessionData = map.get(item.vehicleNumber);
      // if not already set or if a car has run multiple laps in the context of a single leader lap, only show the latest lap
      if (!vehicleSessionData || (vehicleSessionData && vehicleSessionData.lapsCompleted < item.lapsCompleted)) {
        /* The following is not provided in SessionVehicleMetadata but is needed for LapData:
        loopName, isPitLap, pitCycleTimeMetadata, pitPositionMetadata, leaderLap, groupName, sessionInfo, messageInfo
        **/
        map.set(item.vehicleNumber, { ...item, isLuckyDog: false } as unknown as LapData);
      }

      return map;
    },
    new Map<string, LapData>()
  );
  return vehicleLatestLapMap ? Array.from(vehicleLatestLapMap.values()) : [];
});

export const qualifyingLapDataReplayCurrentLapSelector = atom<QualifyingLapData[]>((get) => {
  const selectedLap = get(lapReplayControllerAtom);
  const currentLap = get(unwrappedTimingScoringReplayDataSelector)
    // reduce to most recent leader lap, up to the lapReplayController number, for a given qualifyingRoundName
    ?.reduce((map, item) => {
      const vehicleSessionData = map.get(item.qualifyingRoundName);
      if (
        item.leaderLap <= selectedLap && // only show laps up to the current selected replay lap
        (!vehicleSessionData || (vehicleSessionData && vehicleSessionData.leaderLap < item.leaderLap)) // show most recent leader lap
      ) {
        map.set(item.qualifyingRoundName, item);
      }
      return map;
    }, new Map<string, SessionTimingScoringData>());
  return currentLap ? Array.from(currentLap.values()).flatMap((item) => item.sessionVehicleMetadataList) : [];
});

export const lapDataHistoryReplayDataSelector = atom<LapHistData[]>((get) => {
  const currentLap = get(lapReplayControllerAtom);
  const allData = get(unwrappedTimingScoringReplayDataSelector);
  const lapData = allData?.filter((item) => item.leaderLap <= currentLap);
  const isQualifyingSession = get(activeSessionSelector)?.sessionType === RunType.QUALIFYING;

  const vehicleCompletedLaps = new Map<string, number>();
  return lapData
    ? lapData.map((item) => {
        // get unique metadata for each vehicle
        const metadata = (
          isQualifyingSession
            ? Array.from(
                new Set(
                  // each item is a leader lap in a round
                  lapData?.flatMap((item) => item.sessionVehicleMetadataList).map((item) => JSON.stringify(item))
                )
              )
            : Array.from(
                new Set(
                  // each item is the context of a leader lap in a single session
                  item.sessionVehicleMetadataList?.map((item) => JSON.stringify(item))
                )
              )
        ).map((item) => JSON.parse(item));
        return {
          ...item,
          lap: item.leaderLap,
          vehicleMetaDataList: isQualifyingSession
            ? metadata.filter((element) =>
                Object.hasOwn(item, 'qualifyingRoundName') && item.qualifyingRoundName !== ''
                  ? element.lapsCompleted === item.leaderLap && element.runName.includes(item.qualifyingRoundName)
                  : element.lapsCompleted <= item.leaderLap
              )
            : metadata.reduce((acc, element) => {
                const completedLaps = vehicleCompletedLaps.get(element.vehicleNumber);
                if (!completedLaps || completedLaps < element.lapsCompleted) {
                  vehicleCompletedLaps.set(element.vehicleNumber, element.lapsCompleted);
                  acc.push(element);
                }
                return acc;
              }, []),
          consecutiveFastDataMap: [],
          // Required in LapHistData but not provided in SessionVehicleMetadata
          sessionInfo: {} as SessionInfo,
          messageInfo: {} as MessageInfo,
        } as LapHistData;
      })
    : [];
});
